import { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import Layout from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Plus, Trash2, Save, ChevronDown, ChevronUp, Pencil } from 'lucide-react';

function TradeEditor({ trade, items, onUpdateItems, onRemoveTrade }) {
  const [expanded, setExpanded] = useState(false);
  const [newItem, setNewItem] = useState('');

  const addItem = () => {
    if (!newItem.trim()) return;
    onUpdateItems(trade, [...items, newItem.trim()]);
    setNewItem('');
  };

  const removeItem = (idx) => {
    onUpdateItems(trade, items.filter((_, i) => i !== idx));
  };

  const renameItem = (idx, value) => {
    const updated = [...items];
    updated[idx] = value;
    onUpdateItems(trade, updated);
  };

  return (
    <div className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid={`template-trade-${trade.replace(/[\s/]+/g, '-').toLowerCase()}`}>
      <div className="flex items-center justify-between p-3 cursor-pointer hover:bg-slate-50" onClick={() => setExpanded(!expanded)}>
        <div className="flex items-center gap-2">
          <span className="font-semibold text-sm text-slate-900">{trade}</span>
          <Badge variant="outline" className="text-[10px]">{items.length} items</Badge>
        </div>
        <div className="flex items-center gap-1">
          <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-400 hover:text-red-600" onClick={(e) => { e.stopPropagation(); onRemoveTrade(trade); }} data-testid={`remove-trade-${trade.replace(/[\s/]+/g, '-').toLowerCase()}`}>
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
          {expanded ? <ChevronUp className="h-4 w-4 text-slate-400" /> : <ChevronDown className="h-4 w-4 text-slate-400" />}
        </div>
      </div>
      {expanded && (
        <div className="px-3 pb-3 space-y-1.5">
          {items.map((item, idx) => (
            // Items are plain strings with no stable IDs; compose a key from
            // trade name + current value + index so rename/reorder remains stable.
            <div key={`${trade}__${idx}__${item}`} className="flex items-center gap-2">
              <Input value={item} onChange={(e) => renameItem(idx, e.target.value)} className="h-8 text-sm flex-1" data-testid={`item-input-${idx}`} />
              <Button size="sm" variant="ghost" className="h-8 w-8 p-0 text-red-400 hover:text-red-600" onClick={() => removeItem(idx)}>
                <Trash2 className="h-3 w-3" />
              </Button>
            </div>
          ))}
          <div className="flex items-center gap-2 pt-1">
            <Input value={newItem} onChange={(e) => setNewItem(e.target.value)} placeholder="New item name..." className="h-8 text-sm flex-1" onKeyDown={(e) => e.key === 'Enter' && addItem()} data-testid="new-item-input" />
            <Button size="sm" variant="outline" className="h-8 text-xs" onClick={addItem} data-testid="add-item-button">
              <Plus className="h-3 w-3 mr-1" />Add
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

export default function TemplatePage() {
  const [unitType, setUnitType] = useState('flat');
  const [stageTab, setStageTab] = useState('progress_1');
  const [trades, setTrades] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [newTradeName, setNewTradeName] = useState('');
  const [addTradeOpen, setAddTradeOpen] = useState(false);

  const stageType = stageTab.split('_')[0];
  const stageNumber = parseInt(stageTab.split('_')[1]);

  const fetchTemplate = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await api.get(`/admin/templates/${unitType}/${stageType}/${stageNumber}`);
      setTrades(resp.data.trades || {});
    } catch {
      toast.error('Failed to load template');
    } finally {
      setLoading(false);
    }
  }, [unitType, stageType, stageNumber]);

  useEffect(() => { fetchTemplate(); }, [fetchTemplate]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put(`/admin/templates/${unitType}/${stageType}/${stageNumber}`, { trades });
      toast.success('Template saved');
    } catch {
      toast.error('Save failed');
    } finally {
      setSaving(false);
    }
  };

  const updateTradeItems = (trade, items) => {
    setTrades(prev => ({ ...prev, [trade]: items }));
  };

  const removeTrade = (trade) => {
    setTrades(prev => {
      const next = { ...prev };
      delete next[trade];
      return next;
    });
  };

  const addTrade = () => {
    if (!newTradeName.trim()) return;
    setTrades(prev => ({ ...prev, [newTradeName.trim()]: [] }));
    setNewTradeName('');
    setAddTradeOpen(false);
  };

  const STAGE_TABS = [
    { value: 'progress_1', label: 'Prog 1' }, { value: 'progress_2', label: 'Prog 2' }, { value: 'progress_3', label: 'Prog 3' },
    { value: 'qa_1', label: 'QA 1' }, { value: 'qa_2', label: 'QA 2' }, { value: 'qa_3', label: 'QA 3' },
  ];

  return (
    <Layout>
      <div className="space-y-4" data-testid="template-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Outfit' }}>
              <Pencil className="inline h-5 w-5 mr-2 text-blue-600" />Template Editor
            </h1>
            <p className="text-sm text-slate-500 mt-1">Edit checklist templates for new units</p>
          </div>
          <Button onClick={handleSave} disabled={saving} className="bg-blue-600 hover:bg-blue-700" data-testid="save-template-button">
            <Save className="h-4 w-4 mr-1" />{saving ? 'Saving...' : 'Save'}
          </Button>
        </div>

        {/* Unit type toggle */}
        <div className="flex gap-2">
          <Button size="sm" variant={unitType === 'flat' ? 'default' : 'outline'} className={unitType === 'flat' ? 'bg-blue-600' : ''} onClick={() => setUnitType('flat')} data-testid="unit-type-flat">Flats</Button>
          <Button size="sm" variant={unitType === 'communal' ? 'default' : 'outline'} className={unitType === 'communal' ? 'bg-blue-600' : ''} onClick={() => setUnitType('communal')} data-testid="unit-type-communal">Communal</Button>
        </div>

        {/* Stage tabs */}
        <Tabs value={stageTab} onValueChange={setStageTab}>
          <TabsList className="w-full grid grid-cols-6">
            {STAGE_TABS.map(t => <TabsTrigger key={t.value} value={t.value} data-testid={`stage-tab-${t.value}`}>{t.label}</TabsTrigger>)}
          </TabsList>
        </Tabs>

        {loading ? (
          <div className="text-center py-12 text-slate-400">Loading template...</div>
        ) : (
          <div className="space-y-3">
            {Object.entries(trades).map(([trade, items]) => (
              <TradeEditor key={trade} trade={trade} items={items} onUpdateItems={updateTradeItems} onRemoveTrade={removeTrade} />
            ))}

            {Object.keys(trades).length === 0 && (
              <div className="text-center py-8 text-slate-400">No trades defined for this stage</div>
            )}

            {/* Add trade */}
            <Dialog open={addTradeOpen} onOpenChange={setAddTradeOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" className="w-full border-dashed" data-testid="add-trade-trigger">
                  <Plus className="h-4 w-4 mr-2" />Add Trade
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle>Add New Trade</DialogTitle></DialogHeader>
                <div className="space-y-3">
                  <Input value={newTradeName} onChange={(e) => setNewTradeName(e.target.value)} placeholder="Trade name..." onKeyDown={(e) => e.key === 'Enter' && addTrade()} data-testid="new-trade-name-input" />
                  <Button onClick={addTrade} className="w-full bg-blue-600 hover:bg-blue-700" data-testid="confirm-add-trade">Add Trade</Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        )}
      </div>
    </Layout>
  );
}
